import { type FormEvent, useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { SignupResultLayout } from '../SignupResultLayout/SignupResultLayout';
import { signupCreateAccountStyles } from './SignupCreateAccount.styles';

interface SignupCreateAccountProps {
  verificationHash: string;
  onComplete: (hash: string, firstName: string, lastName: string, password: string) => void;
}

export function SignupCreateAccount({verificationHash, onComplete}: SignupCreateAccountProps) {
  const theme = useTheme();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [completionError, setCompletionError] = useState('');

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const hash = verificationHash.trim();
    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    if (!hash) {
      setCompletionError('This signup link is invalid or has expired.');
      return;
    }
    if (!trimmedFirstName || !trimmedLastName) {
      setCompletionError('Enter your first and last name.');
      return;
    }
    if (password !== passwordConfirmation) {
      setCompletionError('Passwords do not match.');
      return;
    }
    setCompletionError('');
    onComplete(hash, trimmedFirstName, trimmedLastName, password);
  }

  return (
    <SignupResultLayout title="Finish creating your account">
      <Typography sx={signupCreateAccountStyles.resultMessage(theme)}>Enter your name and choose a password.</Typography>
      <Box component="form" onSubmit={handleSubmit} sx={signupCreateAccountStyles.completionForm()}>
        <TextField label="First name" required value={firstName} onChange={(event) => setFirstName(event.target.value)} sx={signupCreateAccountStyles.input()} />
        <TextField label="Last name" required value={lastName} onChange={(event) => setLastName(event.target.value)} sx={signupCreateAccountStyles.input()} />
        <TextField label="Password" type="password" required value={password} onChange={(event) => setPassword(event.target.value)} slotProps={{htmlInput: {'aria-label': 'Password'}}} sx={signupCreateAccountStyles.input()} />
        <TextField label="Confirm password" type="password" required value={passwordConfirmation} onChange={(event) => setPasswordConfirmation(event.target.value)} slotProps={{htmlInput: {'aria-label': 'Confirm password'}}} sx={signupCreateAccountStyles.input()} />
        {completionError && <Typography role="alert" sx={signupCreateAccountStyles.consentError(theme)}>{completionError}</Typography>}
        <Button type="submit" fullWidth variant="contained" disableElevation sx={signupCreateAccountStyles.emailButton(theme)}>Create account</Button>
      </Box>
    </SignupResultLayout>
  );
}
